// src/pages/AdminChatPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BsClipboard,
  BsClipboardCheck,
  BsHandThumbsUp,
  BsHandThumbsUpFill,
  BsHandThumbsDown,
  BsHandThumbsDownFill,
  BsPlusLg,
  BsChevronLeft,
  BsChevronRight,
} from "react-icons/bs";

import {
  askChat,
  createNewChat,
  listChatSessions,
  loadChatSession,
  renameChatSession,
  deleteChatSession,
  sendFeedback,
} from "../api/chatApi";

/* -----------------------------
   Inline CSS for "thinking" dots
   ----------------------------- */
const THINKING_CSS = `
@keyframes aegisThinkingBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.55; }
  40%          { transform: translateY(-5px); opacity: 1; }
}

.aegis-thinking {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 14px;
}

.aegis-thinking-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  animation: aegisThinkingBounce 1.05s infinite ease-in-out;
}

.aegis-thinking-dot:nth-child(1) { animation-delay: 0ms; }
.aegis-thinking-dot:nth-child(2) { animation-delay: 150ms; }
.aegis-thinking-dot:nth-child(3) { animation-delay: 300ms; }
`;

function ThinkingDots() {
  return (
    <span className="aegis-thinking" aria-label="Thinking">
      <span className="aegis-thinking-dot" />
      <span className="aegis-thinking-dot" />
      <span className="aegis-thinking-dot" />
    </span>
  );
}

/* -----------------------------
   Utilities
   ----------------------------- */

// IMPORTANT: We do NOT truncate titles in state anymore.
// Clamp/ellipsis is purely visual (CSS).
const MAX_TITLE_STORE_LEN = 120;

function clampTitleWordSafe(s, maxLen = MAX_TITLE_STORE_LEN) {
  const t = (s ?? "").trim().replace(/\s+/g, " ");
  if (!t) return "";
  if (t.length <= maxLen) return t;

  const cut = t.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  const safe = lastSpace > 20 ? cut.slice(0, lastSpace) : cut;
  return safe.trim() + "…";
}

function looksLikeDefaultTitle(title) {
  const t = (title ?? "").trim().toLowerCase();
  return !t || t === "new chat" || t === "chat" || t === "untitled";
}

function generateTitleFromUserText(text) {
  const raw = (text ?? "").toString();

  const cleaned = raw
    .replace(/```[\s\S]*?```/g, "") // remove code blocks
    .replace(/`[^`]*`/g, "") // remove inline code
    .replace(/[#>*_]/g, "") // strip common markdown markers
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "New chat";

  // Prefer first sentence if reasonable; otherwise first 10–14 words.
  const firstSentence = cleaned.split(/(?<=[.!?])\s/)[0] || cleaned;
  const words = firstSentence.split(" ").filter(Boolean);

  const candidate =
    words.length <= 14 ? firstSentence : words.slice(0, 14).join(" ");

  return clampTitleWordSafe(candidate, MAX_TITLE_STORE_LEN) || "New chat";
}

function formatTimeOnly(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

function formatRuntimeMs(ms) {
  if (ms == null || Number.isNaN(ms)) return "";
  const totalSec = Math.max(0, Math.round(Number(ms) / 1000));
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function roleLabel(role) {
  if (role === "assistant") return "Aegis Assistant";
  if (role === "system") return "System";
  return "You";
}

/* Stable server-side id used to reference an exchange for feedback: freshly
   streamed messages carry it as responseId; history messages use their real
   numeric id (a client "tmp-*" id is not referenceable). */
function feedbackResponseId(m) {
  if (m.responseId != null) return m.responseId;
  return typeof m.id === "number" ? m.id : null;
}

/* Whether the backend stored a usable exemplar for this exchange. Fresh
   messages carry the exemplarEligible flag from the stream; history messages
   carry meta.exemplars. Answers from before this feature (no such data) return
   false so we hide the thumbs entirely rather than let feedback fail. */
function messageIsExemplarEligible(m) {
  if (m.exemplarEligible === true) return true;
  const ex = m.metadata && m.metadata.exemplars;
  return Array.isArray(ex) && ex.some((e) => e && e.exemplar_eligible === true);
}

/* Show 👍/👎 only when the exchange is actually referenceable AND storable. */
function canGiveFeedback(m) {
  return (
    m.role === "assistant" &&
    feedbackResponseId(m) != null &&
    messageIsExemplarEligible(m)
  );
}

/* Fixes "1.\n\nText" => "1. Text" so list numbers don't appear on their own line */
function normalizeMarkdownListMarkers(md) {
  if (!md) return md;

  // Avoid touching code blocks: only rewrite outside ``` fences
  const parts = String(md).split("```");
  for (let i = 0; i < parts.length; i += 2) {
    let s = parts[i];

    // 1.\n\nText  ->  1. Text
    s = s.replace(/(^|\n)([ \t]*\d+\.)[ \t]*\n[ \t]*\n+[ \t]*/g, "$1$2 ");

    // -\n\nText  ->  - Text
    s = s.replace(
      /(^|\n)([ \t]*[-*+])[ \t]*\n[ \t]*\n+[ \t]*/g,
      "$1$2 "
    );

    parts[i] = s;
  }
  return parts.join("```");
}

export default function AdminChatPage({ chatType = "chat" }) {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeTitle, setActiveTitle] = useState("New chat");
  const [messages, setMessages] = useState([]);
  
  
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);

  const [composer, setComposer] = useState("");
  const [errorBanner, setErrorBanner] = useState(null);

  // Inline rename state
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");

  // Hover + menu state for session rows
  const [hoveredSessionId, setHoveredSessionId] = useState(null);
  const [menuSessionId, setMenuSessionId] = useState(null);

  // Sidebar toggle
  const [railOpen, setRailOpen] = useState(true);

  // Scroll management
  const threadRef = useRef(null);
  const [autoStickToBottom, setAutoStickToBottom] = useState(true);
  // Ref to the most recent assistant message row, so we can scroll to its TOP
  const lastAssistantRef = useRef(null);
  const prevAssistantIdRef = useRef(null);

  // SSE ref (so we can close on unmount / new send)
  const sseRef = useRef(null);

  // Prevent React StrictMode double-init in dev
  const initOnceRef = useRef(false);

  // Skip loading thread for newly created chats (we already know it's empty)
  const skipLoadOnceRef = useRef(null);

  // Ensure we only auto-title a session once
  const autoTitledRef = useRef(new Set());

  // Copy feedback (per message)
  const [copiedMsgId, setCopiedMsgId] = useState(null);

  // Thumbs feedback per message id: { [id]: "up" | "down" | "sending" }.
  // Drives button highlight and prevents double-submit.
  const [feedbackByMsgId, setFeedbackByMsgId] = useState({});

  // Ask & Answer v2 (/chat2, "Aegis Ask & Answer") uses the light General-Chat
  // look; the original /chat surface stays on the dark theme.
  const isV2 = chatType === "chat2";
  const theme = isV2 ? LIGHT : DARK;
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const markdownComponents = useMemo(
    () => makeMarkdownComponents(theme),
    [theme]
  );

  // kept (even if unused) from original structure
  const activeSession = useMemo(
    () => sessions.find((s) => s.sessionId === activeSessionId) || null,
    [sessions, activeSessionId]
  );

  // Prevent page (body) scrolling so only rail/thread scroll
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Close menu on outside click / Escape
  useEffect(() => {
    function onDocDown(e) {
      const el = e.target;
      if (!(el instanceof HTMLElement)) return;
      if (el.closest?.("[data-session-menu-root='true']")) return;
      setMenuSessionId(null);
    }
    function onEsc(e) {
      if (e.key === "Escape") setMenuSessionId(null);
    }
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Close SSE stream on unmount
  useEffect(() => {
    return () => {
      try {
        sseRef.current?.close?.();
      } catch {
        // ignore
      } finally {
        sseRef.current = null;
      }
    };
  }, []);

  function handleError(e, fallback) {
    const status = e?.status;
    const msg = e?.message || fallback;

    if (status === 401) {
      setErrorBanner("Your session has expired. Please log in again.");
      return;
    }
    if (status === 502 || status === 500) {
      setErrorBanner("Workflow error (server). Please retry in a moment.");
      return;
    }
    setErrorBanner(msg);
  }

  // Copy assistant message: write both HTML + plain text (format preserved on paste where supported)
  async function copyAssistantMessage(messageId, plainText) {
    const el = document.getElementById(`msg-content-${messageId}`);

    if (!el) {
      await navigator.clipboard.writeText(String(plainText ?? ""));
      setCopiedMsgId(messageId);
      setTimeout(() => setCopiedMsgId(null), 1200);
      return;
    }

    const html = el.innerHTML;

    try {
      if (navigator.clipboard?.write && window.ClipboardItem) {
        const item = new ClipboardItem({
          "text/plain": new Blob([String(plainText ?? "")], { type: "text/plain" }),
          "text/html": new Blob([String(html ?? "")], { type: "text/html" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // older fallback: plain text
        await navigator.clipboard.writeText(String(plainText ?? ""));
      }

      setCopiedMsgId(messageId);
      setTimeout(() => setCopiedMsgId(null), 1200);
    } catch {
      await navigator.clipboard.writeText(String(plainText ?? ""));
      setCopiedMsgId(messageId);
      setTimeout(() => setCopiedMsgId(null), 1200);
    }
  }

  /* -----------------------------------------
     Thumbs feedback on an assistant response.
     Up: stores the (question -> SQL) exemplar; Down: logs for review.
     Sends the stable responseId when present, plus {question, sql, source}
     as a fallback. One rating per message; ignores repeat/second clicks.
     ----------------------------------------- */
  async function submitFeedback(m, rating) {
    const current = feedbackByMsgId[m.id];
    if (current === "sending" || current === "up" || current === "down") return;

    setFeedbackByMsgId((prev) => ({ ...prev, [m.id]: "sending" }));

    try {
      await sendFeedback({
        responseId: feedbackResponseId(m),
        question: m.question ?? null,
        sql: m.sql ?? null,
        source: m.source ?? null,
        sessionId: activeSessionId ?? null,
        rating,
      });
      setFeedbackByMsgId((prev) => ({ ...prev, [m.id]: rating }));
    } catch (e) {
      // Roll back so the user can retry.
      setFeedbackByMsgId((prev) => {
        const next = { ...prev };
        delete next[m.id];
        return next;
      });
      handleError(e, "Failed to submit feedback.");
    }
  }

  /* -----------------------------------------
     Initial load:
       1) load session list
       2) DO NOT auto-create a new chat
          (start user on an UNSAVED draft)
     ----------------------------------------- */
  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;

    const ac = new AbortController();

    (async () => {
      setLoadingSessions(true);
      setErrorBanner(null);

      try {
        const data = await listChatSessions({ limit: 50, surface: chatType, signal: ac.signal });
        const normalized = (data?.sessions ?? []).map((s) => ({
          ...s,
          title: (s.title ?? "New chat").trim(),
        }));
        setSessions(normalized);
      } catch (e) {
        handleError(e, "Failed to load chat sessions.");
      } finally {
        setLoadingSessions(false);
      }

      // Start in a draft (no backend session created)
      setActiveSessionId(null);
      setMessages([]);
      setActiveTitle("New chat");
      setComposer("");
      setAutoStickToBottom(true);
    })();

    return () => ac.abort();
  }, []);

  // When activeSessionId changes, load thread (unless we intentionally skip once)
  useEffect(() => {
    if (!activeSessionId) return;

    if (skipLoadOnceRef.current === activeSessionId) {
      skipLoadOnceRef.current = null;
      setLoadingThread(false);
      return;
    }

    const ac = new AbortController();
    (async () => {
      setLoadingThread(true);
      setErrorBanner(null);
      try {
        const data = await loadChatSession(activeSessionId, {
          signal: ac.signal,
        });

        setActiveTitle((data?.title ?? "").trim() || "Chat");
        setMessages(data?.messages ?? []);
        setAutoStickToBottom(true);

        // Keep sessions list title in sync if server has a newer title
        const serverTitle = (data?.title ?? "").trim();
        if (serverTitle) {
          setSessions((prev) =>
            prev.map((s) =>
              s.sessionId === activeSessionId ? { ...s, title: serverTitle } : s
            )
          );
        }
      } catch (e) {
        if (e?.status === 404) {
          setSessions((prev) => prev.filter((s) => s.sessionId !== activeSessionId));
          setActiveSessionId(null);
          setMessages([]);
          setActiveTitle("New chat");
        }
        handleError(e, "Failed to load chat session.");
      } finally {
        setLoadingThread(false);
      }
    })();

    return () => ac.abort();
  }, [activeSessionId]);

  // Stick to bottom on new messages if autoStickToBottom
  useEffect(() => {
    if (!autoStickToBottom) return;
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, autoStickToBottom, sending]);

  // Id of the most recent assistant message (the "last response")
  const lastAssistantId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return messages[i].id;
    }
    return null;
  }, [messages]);

  // When a NEW assistant response arrives, scroll to the TOP of that response
  // (rather than the bottom) so the user starts reading from the beginning.
  useEffect(() => {
    if (!lastAssistantId) return;
    if (prevAssistantIdRef.current === lastAssistantId) return;
    prevAssistantIdRef.current = lastAssistantId;

    const row = lastAssistantRef.current;
    const container = threadRef.current;
    if (!row || !container) return;

    // Stop auto-sticking so the bottom effect doesn't yank us back down.
    setAutoStickToBottom(false);

    // Align the top of the response just below the top of the thread viewport.
    const rowRect = row.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    container.scrollTop += rowRect.top - containerRect.top - 12;
  }, [lastAssistantId]);

  // New chat: switch to UNSAVED draft (no createNewChat)
  async function onNewChat() {
    setErrorBanner(null);

    // If already in a clean draft, do nothing
    if (!activeSessionId && messages.length === 0 && !composer.trim()) return;

    setActiveSessionId(null);
    setMessages([]);
    setActiveTitle("New chat");
    setComposer("");
    setAutoStickToBottom(true);
    setMenuSessionId(null);
    cancelRename();
  }

  function onSelectSession(sessionId) {
    if (sessionId === activeSessionId) return;
    setActiveSessionId(sessionId);
    setMenuSessionId(null);
  }

  function startRename(session) {
    setEditingSessionId(session.sessionId);
    setDraftTitle(session.title ?? "");
    setMenuSessionId(null);
  }

  function cancelRename() {
    setEditingSessionId(null);
    setDraftTitle("");
  }

  async function commitRename(sessionId) {
    const title = clampTitleWordSafe(draftTitle, MAX_TITLE_STORE_LEN);
    if (!title) {
      cancelRename();
      return;
    }

    // Optimistic update
    setSessions((prev) =>
      prev.map((s) =>
        s.sessionId === sessionId ? { ...s, title, titleUserSet: true } : s
      )
    );
    if (activeSessionId === sessionId) setActiveTitle(title);

    try {
      await renameChatSession(sessionId, title);
    } catch (e) {
      handleError(e, "Failed to rename chat.");
      try {
        const data = await listChatSessions({ limit: 50, surface: chatType });
        const normalized = (data?.sessions ?? []).map((s) => ({
          ...s,
          title: (s.title ?? "New chat").trim(),
        }));
        setSessions(normalized);
      } catch {
        // ignore
      }
    } finally {
      cancelRename();
    }
  }

  async function onDeleteSession(session) {
    setMenuSessionId(null);

    const ok = window.confirm(
      `Delete chat "${session.title || "Untitled"}"? This cannot be undone.`
    );
    if (!ok) return;

    // Optimistic removal
    setSessions((prev) => prev.filter((s) => s.sessionId !== session.sessionId));
    if (activeSessionId === session.sessionId) {
      setActiveSessionId(null);
      setMessages([]);
      setActiveTitle("New chat");
    }

    try {
      await deleteChatSession(session.sessionId);
    } catch (e) {
      handleError(e, "Failed to delete chat.");
      try {
        const data = await listChatSessions({ limit: 50, surface: chatType });
        const normalized = (data?.sessions ?? []).map((s) => ({
          ...s,
          title: (s.title ?? "New chat").trim(),
        }));
        setSessions(normalized);
      } catch {
        // ignore
      }
    }
  }

  function onThreadScroll() {
    const el = threadRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    setAutoStickToBottom(distanceFromBottom < 48);
  }

  async function onSend() {
    const text = composer.trim();
    if (!text || sending) return;

    setErrorBanner(null);
    setSending(true);

    // Close any previous SSE stream
    try {
      sseRef.current?.close?.();
    } catch {
      // ignore
    } finally {
      sseRef.current = null;
    }

    // If we're in a draft (no session yet), create a session NOW
    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const created = await createNewChat({ surface: chatType });
        const newSession = {
          sessionId: created.sessionId,
          title: (created.title ?? "New chat").trim() || "New chat",
          titleUserSet: false,
          createdAt: created.createdAt,
          updatedAt: created.updatedAt,
        };

        skipLoadOnceRef.current = newSession.sessionId;

        setSessions((prev) => [newSession, ...prev]);
        setActiveSessionId(newSession.sessionId);
        setActiveTitle(newSession.title);

        sessionId = newSession.sessionId;
      } catch (e) {
        handleError(e, "Failed to create new chat.");
        setSending(false);
        return;
      }
    }

    const sentText = text; // stable reference for SSE callbacks

    // Optimistically add user message
    setMessages((prev) => [
      ...prev,
      {
        id: `tmp-user-${Date.now()}`,
        role: "user",
        text: sentText,
        metadata: null,
        ts: new Date().toISOString(),
      },
    ]);
    setComposer("");
    setAutoStickToBottom(true);

    let finished = false;
    // Id of the streaming assistant bubble (created on the first `token` event);
    // `done` finalizes it in place. Null until the first token arrives.
    let streamingId = null;

    try {
      // IMPORTANT: use sessionId local var (state may lag)
      const data = await askChat(sessionId, sentText);
      const jobId = data?.jobId;

      if (!jobId) {
        throw Object.assign(new Error("Missing jobId from server."), {
          status: 500,
        });
      }

      // Subscribe to SSE stream for this job
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("access_token") ||
        "";

      const es = new EventSource(
        `/api/chat/jobs/${jobId}/stream?token=${encodeURIComponent(token)}`
      );
      sseRef.current = es;

      const finalize = () => {
        if (finished) return;
        finished = true;
        try {
          es.close();
        } catch {
          // ignore
        }
        if (sseRef.current === es) sseRef.current = null;
        setSending(false);
      };

      // Progressive token streaming: create the assistant bubble on the first
      // token, then append each delta in place. A `done` still follows with the
      // authoritative full text (see below), so this only affects live rendering.
      es.addEventListener("token", (ev) => {
        let text = "";
        try {
          text = JSON.parse(ev.data || "{}")?.text || "";
        } catch {
          return;
        }
        if (!text) return;
        if (!streamingId) {
          streamingId = `stream-${jobId}`;
          setMessages((prev) => [
            ...prev,
            {
              id: streamingId,
              role: "assistant",
              text: "",
              metadata: { streaming: true },
              ts: new Date().toISOString(),
            },
          ]);
        }
        const sid = streamingId;
        setMessages((prev) =>
          prev.map((m) => (m.id === sid ? { ...m, text: m.text + text } : m))
        );
      });

      es.addEventListener("done", (ev) => {
        try {
          const payload = JSON.parse(ev.data || "{}");
          const assistantText = payload?.assistantMessage ?? "";
          const timingMs = payload?.timingMs;

          // Finalize: the `done` payload is authoritative (matches what was
          // persisted + audited). Update the streamed bubble in place if it
          // exists, otherwise append fresh (client that missed tokens).
          const finalFields = {
            role: "assistant",
            text: assistantText,
            metadata: { timingMs },
            ts: new Date().toISOString(),
            // Feedback-loop refs: the stable server message id plus the
            // fallback triple ({question, sql, source}) so a thumbs rating can
            // identify this exchange without a reload.
            responseId: payload?.messageId ?? null,
            question: sentText,
            sql: payload?.sql ?? null,
            source: payload?.source ?? null,
            exemplarEligible: payload?.exemplarEligible === true,
          };
          setMessages((prev) => {
            if (streamingId && prev.some((m) => m.id === streamingId)) {
              return prev.map((m) =>
                m.id === streamingId ? { ...m, ...finalFields } : m
              );
            }
            return [...prev, { id: `tmp-asst-${Date.now()}`, ...finalFields }];
          });

          // Move active session to top + bump updatedAt
          setSessions((prev) => {
            const updatedAt = new Date().toISOString();
            const updated = prev.map((s) =>
              s.sessionId === sessionId ? { ...s, updatedAt } : s
            );
            const active = updated.find((s) => s.sessionId === sessionId);
            const rest = updated.filter((s) => s.sessionId !== sessionId);
            return active ? [active, ...rest] : updated;
          });

          // AUTO-TITLE: only once per session, only if user hasn't set a title and it's still default.
          setSessions((prev) => {
            const s = prev.find((x) => x.sessionId === sessionId);
            if (!s) return prev;

            if (autoTitledRef.current.has(sessionId)) return prev;
            if (s.titleUserSet) return prev;
            if (!looksLikeDefaultTitle(s.title)) return prev;

            const newTitle = generateTitleFromUserText(sentText);

            // Optimistic UI update
            const next = prev.map((x) =>
              x.sessionId === sessionId ? { ...x, title: newTitle } : x
            );

            setActiveTitle(newTitle);
            autoTitledRef.current.add(sessionId);

            // Persist to server
            renameChatSession(sessionId, newTitle)
              .then(() => {
                setSessions((p2) =>
                  p2.map((x) =>
                    x.sessionId === sessionId ? { ...x, title: newTitle } : x
                  )
                );
              })
              .catch(() => {
                autoTitledRef.current.delete(sessionId);
              });

            return next;
          });
        } catch {
          setMessages((prev) => [
            ...prev,
            {
              id: `tmp-sys-${Date.now()}`,
              role: "system",
              text: `Error: Failed to parse stream result.`,
              metadata: null,
              ts: new Date().toISOString(),
            },
          ]);
        } finally {
          finalize();
        }
      });

      es.addEventListener("error", () => {
        setMessages((prev) => [
          ...prev,
          {
            id: `tmp-sys-${Date.now()}`,
            role: "system",
            text: `Error: Stream disconnected or job failed.`,
            metadata: null,
            ts: new Date().toISOString(),
          },
        ]);
        finalize();
      });
    } catch (e) {
      handleError(e, "Failed to send message.");
      setMessages((prev) => [
        ...prev,
        {
          id: `tmp-sys-${Date.now()}`,
          role: "system",
          text: `Error: ${e?.message || "Request failed"}`,
          metadata: null,
          ts: new Date().toISOString(),
        },
      ]);
      setSending(false);
    }
  }

  function onComposerKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  return (
    <div style={styles.shell} className={isV2 ? "aqa-light" : undefined}>
      <style>{THINKING_CSS}</style>

      {/* Left rail */}
      {railOpen ? (
        <div style={styles.rail}>
          <div style={styles.railHeader}>
            <div style={styles.railTitle}>History</div>
            <div style={styles.railActions}>
              <button
                style={styles.railNewChatBtn}
                onClick={onNewChat}
                title="New chat"
              >
                <BsPlusLg style={styles.railNewChatIcon} />
                <span>New Chat</span>
              </button>
              <button
                style={styles.railIconBtn}
                onClick={() => setRailOpen(false)}
                title="Collapse history"
                aria-label="Collapse history"
              >
                <BsChevronLeft />
              </button>
            </div>
          </div>

          {loadingSessions ? (
            <div style={styles.railHint}>Loading…</div>
          ) : sessions.length === 0 ? (
            <div style={styles.railHint}>
              No chats yet.
              <div style={{ marginTop: 8 }}>
                <button style={styles.primaryBtn} onClick={onNewChat}>
                  Start a new chat
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.sessionList}>
              {sessions.map((s) => {
                const isActive = s.sessionId === activeSessionId;
                const isEditing = editingSessionId === s.sessionId;
                const isHovered = hoveredSessionId === s.sessionId;
                const menuOpen = menuSessionId === s.sessionId;

                return (
                  <div
                    key={s.sessionId}
                    style={{
                      ...styles.sessionRow,
                      ...(isHovered && !isActive ? styles.sessionRowHover : {}),
                      ...(isActive ? styles.sessionRowActive : {}),
                    }}
                    onClick={() => !isEditing && onSelectSession(s.sessionId)}
                    onMouseEnter={() => setHoveredSessionId(s.sessionId)}
                    onMouseLeave={() =>
                      setHoveredSessionId((prev) =>
                        prev === s.sessionId ? null : prev
                      )
                    }
                  >
                    <div style={styles.sessionMain}>
                      {isEditing ? (
                        <input
                          autoFocus
                          value={draftTitle}
                          onChange={(e) => setDraftTitle(e.target.value)}
                          onBlur={() => commitRename(s.sessionId)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename(s.sessionId);
                            if (e.key === "Escape") cancelRename();
                          }}
                          style={styles.renameInput}
                        />
                      ) : (
                        <>
                          <div style={styles.sessionTitle} title={s.title || "Untitled"}>
                            {s.title || "Untitled"}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Ellipsis appears on hover */}
                    {!isEditing && (isHovered || menuOpen) && (
                      <div
                        data-session-menu-root="true"
                        style={styles.menuRoot}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          style={styles.ellipsisBtn}
                          title="Menu"
                          onClick={() =>
                            setMenuSessionId((prev) =>
                              prev === s.sessionId ? null : s.sessionId
                            )
                          }
                        >
                          ⋯
                        </button>

                        {menuOpen && (
                          <div style={styles.menu}>
                            <button style={styles.menuItem} onClick={() => startRename(s)}>
                              Rename
                            </button>
                            <button
                              style={{ ...styles.menuItem, ...styles.menuItemDanger }}
                              onClick={() => onDeleteSession(s)}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <button
          style={styles.railExpand}
          onClick={() => setRailOpen(true)}
          title="Show history"
          aria-label="Show history"
        >
          <BsChevronRight />
        </button>
      )}

      {/* Right pane */}
      <div style={styles.main}>
        <div style={styles.topBar}>
          <h3
            className="mb-0"
            style={{
              margin: 0,
              color: theme.cText,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {isV2
              ? "Ask & Answer"
              : activeSessionId
              ? activeTitle || "Chat"
              : "New chat"}
          </h3>
        </div>

        {errorBanner && <div style={styles.errorBanner}>{errorBanner}</div>}

        {/* Thread */}
        <div style={styles.thread} ref={threadRef} onScroll={onThreadScroll}>
          {!activeSessionId ? (
            <div style={styles.emptyState}>
              Ask a question to start talking with your data
            </div>
          ) : loadingThread ? (
            <div style={styles.railHint}>Loading messages…</div>
          ) : messages.length === 0 ? (
            <div style={styles.emptyState}>
              Ask a question to start talking with your data
            </div>
          ) : (
            <div style={styles.msgList}>
              {messages.map((m) => {
                const isUser = m.role === "user";
                const isAssistant = m.role === "assistant";
                const isSystem = m.role === "system";

                const rowAlign = isUser ? styles.msgRowRight : styles.msgRowLeft;

                return (
                  <div
                    key={m.id}
                    ref={m.id === lastAssistantId ? lastAssistantRef : null}
                    style={{ ...styles.msgRow, ...rowAlign }}
                  >
                    <div
                      style={{
                        ...styles.msgHeader,
                        ...(isUser ? styles.msgHeaderRight : styles.msgHeaderLeft),
                      }}
                    >
                      <span style={styles.msgRole}>{roleLabel(m.role)}</span>
                      {m.ts && <span style={styles.msgTs}>{formatTimeOnly(m.ts)}</span>}
                      {m?.metadata?.timingMs != null && (
                        <span style={styles.msgTs}> · {formatRuntimeMs(m.metadata.timingMs)}</span>
                      )}
                    </div>

                    <div
                      style={{
                        ...styles.msgBubble,
                        ...(isUser ? styles.msgBubbleUser : {}),
                        ...(isAssistant ? styles.msgBubbleAssistant : {}),
                        ...(isSystem ? styles.msgBubbleSystem : {}),
                      }}
                    >
                      {isAssistant ? (
                        <div style={styles.assistantWrap}>
                          {/* FLOATING icon button */}
                          <button
                            type="button"
                            style={styles.copyIconBtnFloating}
                            onClick={(e) => {
                              e.stopPropagation();
                              copyAssistantMessage(m.id, m.text);
                            }}
                            title={copiedMsgId === m.id ? "Copied" : "Copy"}
                            aria-label="Copy response"
                          >
                            {copiedMsgId === m.id ? (
                              <BsClipboardCheck style={styles.copyIcon} />
                            ) : (
                              <BsClipboard style={styles.copyIcon} />
                            )}
                          </button>

                          {/* Region we copy as HTML */}
                          <div id={`msg-content-${m.id}`}>
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {normalizeMarkdownListMarkers(m.text)}
                            </ReactMarkdown>
                          </div>

                          {/* END-OF-RESPONSE action row: feedback + copy */}
                          <div style={styles.copyFooter}>
                            {/* Thumbs feedback (validated-exemplar loop) — only
                                shown when the exchange is stored & referenceable. */}
                            {canGiveFeedback(m) ? (
                              <div style={styles.feedbackGroup}>
                                <button
                                  type="button"
                                  style={{
                                    ...styles.feedbackBtn,
                                    ...(feedbackByMsgId[m.id] === "up"
                                      ? styles.feedbackBtnActiveUp
                                      : {}),
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    submitFeedback(m, "up");
                                  }}
                                  disabled={!!feedbackByMsgId[m.id]}
                                  title="Good answer"
                                  aria-label="Good answer"
                                  aria-pressed={feedbackByMsgId[m.id] === "up"}
                                >
                                  {feedbackByMsgId[m.id] === "up" ? (
                                    <BsHandThumbsUpFill style={styles.copyIcon} />
                                  ) : (
                                    <BsHandThumbsUp style={styles.copyIcon} />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  style={{
                                    ...styles.feedbackBtn,
                                    ...(feedbackByMsgId[m.id] === "down"
                                      ? styles.feedbackBtnActiveDown
                                      : {}),
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    submitFeedback(m, "down");
                                  }}
                                  disabled={!!feedbackByMsgId[m.id]}
                                  title="Needs work"
                                  aria-label="Needs work"
                                  aria-pressed={feedbackByMsgId[m.id] === "down"}
                                >
                                  {feedbackByMsgId[m.id] === "down" ? (
                                    <BsHandThumbsDownFill style={styles.copyIcon} />
                                  ) : (
                                    <BsHandThumbsDown style={styles.copyIcon} />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <span />
                            )}

                            <button
                              type="button"
                              style={styles.copyIconBtnFooter}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyAssistantMessage(m.id, m.text);
                              }}
                              title={copiedMsgId === m.id ? "Copied" : "Copy"}
                              aria-label="Copy response"
                            >
                              {copiedMsgId === m.id ? (
                                <BsClipboardCheck style={styles.copyIcon} />
                              ) : (
                                <BsClipboard style={styles.copyIcon} />
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        m.text
                      )}
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div style={{ ...styles.msgRow, ...styles.msgRowLeft }}>
                  <div style={{ ...styles.msgHeader, ...styles.msgHeaderLeft }}>
                    <span style={styles.msgRole}>Aegis Assistant</span>
                    <span style={styles.msgTs}> · thinking</span>
                  </div>
                  <div style={{ ...styles.msgBubble, ...styles.msgBubbleAssistant }}>
                    <ThinkingDots />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Composer */}
        <div style={styles.composer}>
          <input
            type="text"
            style={styles.textarea}
            placeholder="Type a message…"
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={onComposerKeyDown}
            disabled={sending}
          />
          <button
            style={{
              ...styles.sendBtn,
              ...(sending || !composer.trim() ? styles.sendBtnDisabled : {}),
            }}
            onClick={onSend}
            disabled={sending || !composer.trim()}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

/* -----------------------------
   Markdown rendering overrides
   (tighter spacing)
   ----------------------------- */
const makeMarkdownComponents = (t) => ({
  h1: ({ children }) => (
    <div style={{ fontSize: 18, fontWeight: 800, margin: "4px 0 6px" }}>
      {children}
    </div>
  ),
  h2: ({ children }) => (
    <div style={{ fontSize: 16, fontWeight: 800, margin: "4px 0 6px" }}>
      {children}
    </div>
  ),
  h3: ({ children }) => (
    <div style={{ fontSize: 14, fontWeight: 800, margin: "4px 0 6px" }}>
      {children}
    </div>
  ),
  p: ({ children }) => <div style={{ margin: "3px 0" }}>{children}</div>,
  ul: ({ children }) => <ul style={{ margin: "3px 0 3px 16px" }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: "3px 0 3px 16px" }}>{children}</ol>,
  li: ({ children }) => <li style={{ margin: "1px 0" }}>{children}</li>,
  blockquote: ({ children }) => (
    <div
      style={{
        margin: "6px 0",
        padding: "6px 10px",
        borderLeft: `3px solid ${t.quoteBar}`,
        background: t.quoteBg,
        borderRadius: 10,
      }}
    >
      {children}
    </div>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: "auto", marginTop: 6 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th
      style={{
        textAlign: "left",
        padding: "7px 8px",
        borderBottom: `1px solid ${t.tableHeadLine}`,
        color: t.text,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        padding: "7px 8px",
        borderBottom: `1px solid ${t.tableLine}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  ),
  code: ({ children }) => (
    <code
      style={{
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 12,
        padding: "2px 6px",
        borderRadius: 8,
        background: t.codeBg,
        border: `1px solid ${t.codeBorder}`,
      }}
    >
      {children}
    </code>
  ),
});

/* -----------------------------
   Styles
   ----------------------------- */
// Dark palette (original Ask & Answer look — used by /chat).
const DARK = {
  bg: "#0b0f19",
  text: "#e7eaf3",
  railBg: "#070a12",
  line: "rgba(255,255,255,0.08)",
  lineStrong: "rgba(255,255,255,0.16)",
  lineSoft: "rgba(255,255,255,0.06)",
  panel: "rgba(255,255,255,0.03)",
  panelSoft: "rgba(255,255,255,0.02)",
  panelHover: "rgba(255,255,255,0.06)",
  btnBg: "rgba(255,255,255,0.06)",
  inputBg: "rgba(255,255,255,0.03)",
  iconBtnBg: "rgba(0,0,0,0.18)",
  iconColor: "rgba(231,234,243,0.92)",
  muted: "rgba(231,234,243,0.70)",
  muted2: "rgba(231,234,243,0.65)",
  muted3: "rgba(231,234,243,0.75)",
  menuBg: "#0b0f19",
  composerBg: "#0b0f19",
  // chrome = rail + top bar. On the fully-dark surface these match the content.
  topBarBg: "#0b0f19",
  cText: "#e7eaf3",
  cMuted: "rgba(231,234,243,0.70)",
  cLine: "rgba(255,255,255,0.08)",
  cLineStrong: "rgba(255,255,255,0.16)",
  cBtnBg: "rgba(255,255,255,0.06)",
  cRowBg: "transparent",
  cRowBorder: "transparent",
  cRowHover: "rgba(255,255,255,0.06)",
  cActiveBg: "rgba(255,255,255,0.06)",
  cActiveBorder: "rgba(255,255,255,0.18)",
  cInputBg: "rgba(255,255,255,0.04)",
  cMenuBg: "#0b0f19",
  cIcon: "rgba(231,234,243,0.9)",
  cDanger: "#ffb3b3",
  // card layout (v1 stays flush: no page inset, no card treatment)
  pageBg: "#0b0f19",
  shellHeight: "calc(100vh - 56px)",
  pad: 0,
  gap: 0,
  cardBg: "transparent",
  cardBorder: "transparent",
  cardRadius: 0,
  topBarLine: "rgba(255,255,255,0.08)",
  composerPad: 12,
  userBg: "rgba(80,160,255,0.08)",
  userBorder: "rgba(80,160,255,0.22)",
  asstBg: "rgba(160,120,255,0.08)",
  asstBorder: "rgba(160,120,255,0.22)",
  sysBg: "rgba(255,180,80,0.08)",
  sysBorder: "rgba(255,180,80,0.22)",
  errBg: "rgba(255, 80, 80, 0.12)",
  errText: "#ffd0d0",
  danger: "#ffb3b3",
  upBorder: "rgba(52,199,123,0.7)",
  upBg: "rgba(52,199,123,0.18)",
  upColor: "rgba(120,230,170,1)",
  downBorder: "rgba(235,110,110,0.7)",
  downBg: "rgba(235,110,110,0.18)",
  downColor: "rgba(245,150,150,1)",
  // markdown
  quoteBar: "rgba(255,255,255,0.18)",
  quoteBg: "rgba(255,255,255,0.03)",
  tableHeadLine: "rgba(255,255,255,0.14)",
  tableLine: "rgba(255,255,255,0.08)",
  codeBg: "rgba(255,255,255,0.06)",
  codeBorder: "rgba(255,255,255,0.10)",
};

// Light palette (matches General Chat — white surfaces, dark/colored text).
// Used by /chat2 ("Aegis Ask & Answer").
const LIGHT = {
  bg: "#ffffff",
  text: "#1b2430",
  railBg: "#0f172a",
  line: "rgba(0,0,0,0.10)",
  lineStrong: "rgba(0,0,0,0.16)",
  lineSoft: "rgba(0,0,0,0.06)",
  panel: "#ffffff",
  panelSoft: "#fbfbfc",
  panelHover: "rgba(41,121,255,0.08)",
  btnBg: "rgba(0,0,0,0.04)",
  inputBg: "#ffffff",
  iconBtnBg: "rgba(0,0,0,0.04)",
  iconColor: "rgba(27,36,48,0.75)",
  muted: "rgba(27,36,48,0.62)",
  muted2: "rgba(27,36,48,0.55)",
  muted3: "rgba(27,36,48,0.62)",
  menuBg: "#ffffff",
  composerBg: "transparent",
  // card layout: white rounded transcript/composer float on the dark page (GC look)
  pageBg: "#0b1220",
  shellHeight: "calc(100vh - 96px)",
  pad: 0,
  gap: 14,
  cardBg: "#ffffff",
  cardBorder: "#26324f",
  cardRadius: 14,
  topBarLine: "transparent",
  composerPad: 0,
  // chrome = rail + top bar: General Chat's dark frame over the light content.
  topBarBg: "transparent",
  cText: "#e6eefc",
  cMuted: "#a7b3cd",
  cLine: "#26324f",
  cLineStrong: "#3a4a6b",
  cBtnBg: "rgba(255,255,255,0.06)",
  cRowBg: "transparent",
  cRowBorder: "transparent",
  cRowHover: "rgba(255,255,255,0.06)",
  cActiveBg: "rgba(41,121,255,0.16)",
  cActiveBorder: "rgba(41,121,255,0.45)",
  cInputBg: "rgba(15,23,42,0.7)",
  cMenuBg: "#0f172a",
  cIcon: "rgba(230,238,252,0.9)",
  cDanger: "#ff9b9b",
  userBg: "rgba(41,121,255,0.10)",
  userBorder: "rgba(41,121,255,0.35)",
  asstBg: "#f7f8fa",
  asstBorder: "rgba(0,0,0,0.12)",
  sysBg: "rgba(255,196,84,0.18)",
  sysBorder: "rgba(220,150,30,0.40)",
  errBg: "rgba(220,50,50,0.08)",
  errText: "#8a1c1c",
  danger: "#b42318",
  upBorder: "rgba(34,160,94,0.6)",
  upBg: "rgba(52,199,123,0.15)",
  upColor: "rgba(21,115,71,1)",
  downBorder: "rgba(200,60,60,0.6)",
  downBg: "rgba(235,110,110,0.14)",
  downColor: "rgba(150,30,30,1)",
  // markdown
  quoteBar: "rgba(41,121,255,0.5)",
  quoteBg: "rgba(0,0,0,0.03)",
  tableHeadLine: "rgba(0,0,0,0.18)",
  tableLine: "rgba(0,0,0,0.10)",
  codeBg: "rgba(0,0,0,0.05)",
  codeBorder: "rgba(0,0,0,0.10)",
};

const makeStyles = (t) => ({
  shell: {
    display: "flex",
    height: t.shellHeight,
    width: "100%",
    background: t.pageBg,
    color: t.text,
    fontFamily:
      "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    overflow: "hidden",
    minHeight: 0,
    gap: t.gap,
    padding: t.pad,
    boxSizing: "border-box",
  },
  rail: {
    width: 320,
    flexShrink: 0,
    border: `1px solid ${t.cLine}`,
    borderRadius: t.cardRadius,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background: t.railBg,
    color: t.cText,
    minHeight: 0,
  },
  railHeader: {
    padding: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${t.cLine}`,
  },
  railTitle: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: t.cMuted,
  },
  railActions: { display: "inline-flex", alignItems: "center", gap: 6 },
  railNewChatBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid rgba(41,121,255,0.45)",
    background: "rgba(41,121,255,0.16)",
    color: t.cText,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  railNewChatIcon: { fontSize: 12, display: "block" },
  railIconBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "1px solid transparent",
    background: "transparent",
    color: t.cText,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  },
  railExpand: {
    flexShrink: 0,
    width: 30,
    alignSelf: "flex-start",
    marginTop: 12,
    marginLeft: 8,
    height: 38,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${t.cLine}`,
    borderRadius: 8,
    background: t.railBg,
    color: t.cText,
    cursor: "pointer",
  },
  newChatBtn: {
    padding: "6px 10px",
    borderRadius: 8,
    border: `1px solid ${t.cLineStrong}`,
    background: "transparent",
    color: t.cText,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  railHint: {
    padding: 12,
    color: t.cMuted,
    fontSize: 13,
  },
  primaryBtn: {
    padding: "8px 12px",
    borderRadius: 10,
    border: `1px solid ${t.cLineStrong}`,
    background: t.cBtnBg,
    color: t.cText,
    cursor: "pointer",
  },
  sessionList: {
    overflowY: "auto",
    padding: 8,
    flex: 1,
    minHeight: 0,
  },
  sessionRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 8,
    cursor: "pointer",
    marginBottom: 2,
    border: `1px solid ${t.cRowBorder}`,
    background: t.cRowBg,
    position: "relative",
  },
  sessionRowHover: {
    background: t.cRowHover,
  },
  sessionRowActive: {
    border: `1px solid ${t.cActiveBorder}`,
    background: t.cActiveBg,
  },
  sessionMain: { flex: 1, minWidth: 0 },

  // Two-line clamp + ellipsis purely for display (title stored in full)
  sessionTitle: {
    fontSize: 14,
    fontWeight: 400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.2,
  },
  sessionMeta: {
    fontSize: 12,
    marginTop: 4,
    color: t.cMuted,
  },
  renameInput: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 10,
    border: `1px solid ${t.cLineStrong}`,
    background: t.cInputBg,
    color: t.cText,
    outline: "none",
  },

  menuRoot: { position: "relative" },
  ellipsisBtn: {
    width: 34,
    height: 30,
    borderRadius: 10,
    border: `1px solid ${t.cLine}`,
    background: "transparent",
    color: t.cIcon,
    cursor: "pointer",
    fontSize: 18,
    lineHeight: "18px",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: 36,
    minWidth: 140,
    borderRadius: 12,
    border: `1px solid ${t.cLineStrong}`,
    background: t.cMenuBg,
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    padding: 6,
    zIndex: 50,
  },
  menuItem: {
    width: "100%",
    textAlign: "left",
    padding: "10px 10px",
    borderRadius: 10,
    border: "none",
    background: "transparent",
    color: t.cText,
    cursor: "pointer",
    fontSize: 13,
  },
  menuItemDanger: { color: t.cDanger },

  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
  },
  topBar: {
    padding: "4px 4px",
    marginBottom: t.gap,
    borderBottom: `1px solid ${t.topBarLine}`,
    background: t.topBarBg,
    color: t.cText,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  topBarTitle: {
    fontWeight: 800,
    fontSize: 24,
    color: t.cText,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  toggleRailBtn: {
    width: 34,
    height: 30,
    borderRadius: 10,
    border: `1px solid ${t.cLineStrong}`,
    background: t.cBtnBg,
    color: t.cText,
    cursor: "pointer",
    fontSize: 16,
    lineHeight: "16px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  errorBanner: {
    padding: "10px 16px",
    borderBottom: `1px solid ${t.line}`,
    background: t.errBg,
    color: t.errText,
    fontSize: 13,
    flexShrink: 0,
  },

  thread: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    minHeight: 0,
    background: t.cardBg,
    border: `1px solid ${t.cardBorder}`,
    borderRadius: t.cardRadius,
    marginBottom: t.gap,
  },
  emptyState: {
    color: t.muted,
    fontSize: 14,
  },

  msgList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxWidth: 980,
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  msgRow: { display: "flex", flexDirection: "column", gap: 4 },
  msgRowLeft: { alignItems: "flex-start" },
  msgRowRight: { alignItems: "flex-end" },

  msgHeader: { fontSize: 12, color: t.muted },
  msgHeaderLeft: { textAlign: "left" },
  msgHeaderRight: { textAlign: "right" },

  msgRole: { fontWeight: 700 },
  msgTs: { marginLeft: 8 },

  msgBubble: {
    padding: "10px 12px",
    borderRadius: 16,
    border: `1px solid ${t.line}`,
    background: t.panel,
    whiteSpace: "pre-wrap",
    lineHeight: 1.25,
    maxWidth: 760,
    overflowX: "auto",
  },
  msgBubbleUser: {
    border: `1px solid ${t.userBorder}`,
    background: t.userBg,
  },
  msgBubbleAssistant: {
    border: `1px solid ${t.asstBorder}`,
    background: t.asstBg,
  },
  msgBubbleSystem: {
    border: `1px solid ${t.sysBorder}`,
    background: t.sysBg,
  },

  // --- copy UI ---
  assistantWrap: {
    position: "relative",
    paddingRight: 42, // reserve space so the floating icon doesn't overlap text
  },
  copyIcon: {
    fontSize: 16,
    lineHeight: "16px",
    display: "block",
  },
  copyIconBtnFloating: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 32,
    height: 32,
    borderRadius: 10,
    border: `1px solid ${t.lineStrong}`,
    background: t.iconBtnBg,
    color: t.iconColor,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  copyFooter: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  copyIconBtnFooter: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: `1px solid ${t.lineStrong}`,
    background: t.iconBtnBg,
    color: t.iconColor,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  feedbackGroup: {
    display: "inline-flex",
    gap: 8,
  },
  feedbackBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: `1px solid ${t.lineStrong}`,
    background: t.iconBtnBg,
    color: t.iconColor,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  feedbackBtnActiveUp: {
    borderColor: t.upBorder,
    background: t.upBg,
    color: t.upColor,
    cursor: "default",
  },
  feedbackBtnActiveDown: {
    borderColor: t.downBorder,
    background: t.downBg,
    color: t.downColor,
    cursor: "default",
  },

  composer: {
    padding: t.composerPad,
    borderTop: `1px solid ${t.topBarLine}`,
    display: "flex",
    gap: 10,
    alignItems: "center",
    background: t.composerBg,
    flexShrink: 0,
  },
  textarea: {
    flex: 1,
    height: 44,
    padding: "10px 14px",
    borderRadius: 12,
    border: `1px solid ${t.lineStrong}`,
    background: t.inputBg,
    color: t.text,
    outline: "none",
    fontSize: 14,
    fontFamily: "inherit",
  },
  sendBtn: {
    height: 44,
    padding: "0 18px",
    borderRadius: 12,
    border: "1px solid #2979ff",
    background: "#2979ff",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 600,
  },
  sendBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
});

